import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, Cpu, Trophy, RefreshCw, ArrowLeft, Info } from "lucide-react";
import GameOptions from "./GameOptions";
import PlayerNameInput from "./PlayerNameInput";
import { useGameBot } from "./useGameBot";

const BOARD_SIZE = 8;
const DIRECTIONS = [
    [-1, 0], [1, 0], [0, -1], [0, 1], // Cardinal
    [-1, -1], [-1, 1], [1, -1], [1, 1] // Diagonal
];

// Heuristic Weights
const WEIGHTS = [
    [100, -20, 10, 5, 5, 10, -20, 100],
    [-20, -50, -2, -2, -2, -2, -50, -20],
    [10, -2, -1, -1, -1, -1, -2, 10],
    [5, -2, -1, -1, -1, -1, -2, 5],
    [5, -2, -1, -1, -1, -1, -2, 5],
    [10, -2, -1, -1, -1, -1, -2, 10],
    [-20, -50, -2, -2, -2, -2, -50, -20],
    [100, -20, 10, 5, 5, 10, -20, 100],
];

const ReversiGame = ({ onBack }) => {
    const navigate = useNavigate();

    // Game Settings
    const [gameMode, setGameMode] = useState("PvC");
    const [difficulty, setDifficulty] = useState("Easy");
    const [player1Name, setPlayer1Name] = useState("Player 1");
    const [player2Name, setPlayer2Name] = useState("Player 2");
    const [showRules, setShowRules] = useState(false);

    // Game State
    const [board, setBoard] = useState(Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE).fill(null)));
    const [currentPlayer, setCurrentPlayer] = useState('black');
    const [scores, setScores] = useState({ black: 2, white: 2 });
    const [gameStarted, setGameStarted] = useState(false);
    const [gameOver, setGameOver] = useState(false);
    const [winner, setWinner] = useState(null);
    const [validMoves, setValidMoves] = useState([]);

    // ------------------------------------------------------------------------
    // Core Game Logic
    // ------------------------------------------------------------------------

    const initializeGame = useCallback(() => {
        const newBoard = Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE).fill(null));
        const center = BOARD_SIZE / 2;
        newBoard[center - 1][center - 1] = 'white';
        newBoard[center][center] = 'white';
        newBoard[center - 1][center] = 'black';
        newBoard[center][center - 1] = 'black';

        setBoard(newBoard);
        setCurrentPlayer('black');
        setScores({ black: 2, white: 2 });
        setGameOver(false);
        setWinner(null);
        setValidMoves(calculateValidMoves(newBoard, 'black'));
        setGameStarted(true);
    }, []);

    const getPlayerName = (curr) => {
        if (gameMode === 'PvC' && curr === 'white') return "Computer";
        return curr === 'black' ? player1Name : player2Name;
    };

    const getFlippedPieces = (currentBoard, row, col, player) => {
        if (currentBoard[row][col] !== null) return [];

        let flipped = [];
        const opponent = player === 'black' ? 'white' : 'black';

        for (let [dr, dc] of DIRECTIONS) {
            let r = row + dr;
            let c = col + dc;
            let potentialFlips = [];

            while (r >= 0 && r < BOARD_SIZE && c >= 0 && c < BOARD_SIZE && currentBoard[r][c] === opponent) {
                potentialFlips.push({ r, c });
                r += dr;
                c += dc;
            }

            if (r >= 0 && r < BOARD_SIZE && c >= 0 && c < BOARD_SIZE && currentBoard[r][c] === player && potentialFlips.length > 0) {
                flipped = [...flipped, ...potentialFlips];
            }
        }
        return flipped;
    };

    const calculateValidMoves = (currentBoard, player) => {
        const moves = [];
        for (let i = 0; i < BOARD_SIZE; i++) {
            for (let j = 0; j < BOARD_SIZE; j++) {
                if (getFlippedPieces(currentBoard, i, j, player).length > 0) {
                    moves.push({ r: i, c: j });
                }
            }
        }
        return moves;
    };

    // ------------------------------------------------------------------------
    // AI Logic (Minimax)
    // ------------------------------------------------------------------------

    const evaluateBoard = (b, player) => {
        let score = 0;
        const opponent = player === 'black' ? 'white' : 'black';
        
        // Material & Position difference
        for (let r = 0; r < BOARD_SIZE; r++) {
            for (let c = 0; c < BOARD_SIZE; c++) {
                if (b[r][c] === player) score += WEIGHTS[r][c];
                else if (b[r][c] === opponent) score -= WEIGHTS[r][c];
            }
        }
        
        // Mobility
        const myMoves = calculateValidMoves(b, player).length;
        const oppMoves = calculateValidMoves(b, opponent).length;
        score += (myMoves - oppMoves) * 5;

        return score;
    };

    const minimax = (b, depth, alpha, beta, maximizingPlayer, currentPlayerColor) => {
        const opponentColor = currentPlayerColor === 'black' ? 'white' : 'black';
        const validMoves = calculateValidMoves(b, maximizingPlayer ? currentPlayerColor : opponentColor);
        
        if (depth === 0 || validMoves.length === 0) {
            // If game over or depth reached
            // Note: validMoves.length === 0 isn't strictly game over (pass is possible), 
            // but for simple minimax recursion handling passing adds complexity. 
            // We treat "no moves" leaf as eval state.
            return { score: evaluateBoard(b, currentPlayerColor) };
        }

        if (maximizingPlayer) {
            let maxEval = -Infinity;
            let bestMove = validMoves[Math.floor(Math.random() * validMoves.length)];
            
            for (let move of validMoves) {
                const bCopy = b.map(row => [...row]);
                const flipped = getFlippedPieces(bCopy, move.r, move.c, currentPlayerColor);
                
                // Execute move on copy
                bCopy[move.r][move.c] = currentPlayerColor;
                flipped.forEach(({r, c}) => bCopy[r][c] = currentPlayerColor);

                const evalScore = minimax(bCopy, depth - 1, alpha, beta, false, currentPlayerColor).score;
                
                if (evalScore > maxEval) {
                    maxEval = evalScore;
                    bestMove = move;
                }
                alpha = Math.max(alpha, evalScore);
                if (beta <= alpha) break;
            }
            return { move: bestMove, score: maxEval };
        } else {
            let minEval = Infinity;
            let bestMove = validMoves[Math.floor(Math.random() * validMoves.length)];

            for (let move of validMoves) {
                const bCopy = b.map(row => [...row]);
                const flipped = getFlippedPieces(bCopy, move.r, move.c, opponentColor);
                
                // Execute move on copy
                bCopy[move.r][move.c] = opponentColor;
                flipped.forEach(({r, c}) => bCopy[r][c] = opponentColor);

                const evalScore = minimax(bCopy, depth - 1, alpha, beta, true, currentPlayerColor).score;
                
                if (evalScore < minEval) {
                    minEval = evalScore;
                    bestMove = move;
                }
                beta = Math.min(beta, evalScore);
                if (beta <= alpha) break;
            }
            return { move: bestMove, score: minEval };
        }
    };

    const getBotMove = useCallback((diff, gameState) => {
        const { board, currentPlayer } = gameState;
        const moves = calculateValidMoves(board, currentPlayer);
        
        if (moves.length === 0) return null;

        if (diff === "Easy") {
            // Random move
            return moves[Math.floor(Math.random() * moves.length)];
        } else if (diff === "Medium") {
            // Greedy: maximize flips
            let bestMoves = [];
            let maxFlips = -1;
            
            moves.forEach(move => {
                const flipped = getFlippedPieces(board, move.r, move.c, currentPlayer);
                if (flipped.length > maxFlips) {
                    maxFlips = flipped.length;
                    bestMoves = [move];
                } else if (flipped.length === maxFlips) {
                    bestMoves.push(move);
                }
            });
            return bestMoves[Math.floor(Math.random() * bestMoves.length)];
        } else {
            // Hard: Minimax with Alpha-Beta
            // Depth 3 is usually enough for responsiveness in JS without web workers
            const result = minimax(board, 3, -Infinity, Infinity, true, currentPlayer);
            return result.move || moves[0];
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ------------------------------------------------------------------------
    // Move Execution
    // ------------------------------------------------------------------------

    const executeMove = useCallback((move) => {
        if (!move) return; // Should handle pass turn logic elsewhere if needed, but here move is expected
        
        const { r: row, c: col } = move;

        setBoard(prevBoard => {
            const newBoard = prevBoard.map(arr => [...arr]);
            const flipped = getFlippedPieces(newBoard, row, col, currentPlayer);
            
            newBoard[row][col] = currentPlayer;
            flipped.forEach(({r, c}) => newBoard[r][c] = currentPlayer);
            
            // Check next state
            const nextPlayer = currentPlayer === 'black' ? 'white' : 'black';
            let nextValidMoves = calculateValidMoves(newBoard, nextPlayer);
            let finalNextPlayer = nextPlayer;
            let finalGameOver = false;
            let finalWinner = null;

            // Handle turn passing
            if (nextValidMoves.length === 0) {
                // If next player has no moves, check if current player has moves
                const currentHasMoves = calculateValidMoves(newBoard, currentPlayer).length > 0;
                if (!currentHasMoves) {
                    // Both have no moves -> Game Over
                    finalGameOver = true;
                } else {
                    // Pass turn back to current player
                    finalNextPlayer = currentPlayer; 
                    nextValidMoves = calculateValidMoves(newBoard, currentPlayer); // Refresh valid moves for same player
                    
                    // Alert or visual indicator for pass could go here
                }
            }

            // Calculate scores
            let bScore = 0;
            let wScore = 0;
            for(let r=0; r<BOARD_SIZE; r++) {
                for(let c=0; c<BOARD_SIZE; c++) {
                    if (newBoard[r][c] === 'black') bScore++;
                    if (newBoard[r][c] === 'white') wScore++;
                }
            }
            setScores({ black: bScore, white: wScore });

            if (bScore + wScore === 64 || finalGameOver) {
                finalGameOver = true;
                if (bScore > wScore) finalWinner = 'black';
                else if (wScore > bScore) finalWinner = 'white';
                else finalWinner = 'draw';
            }

            setGameOver(finalGameOver);
            setWinner(finalWinner);
            setCurrentPlayer(finalNextPlayer);
            setValidMoves(nextValidMoves);

            return newBoard;
        });

    }, [currentPlayer]);

    // Bot hook
    const { isBotThinking } = useGameBot({
        gameMode,
        makeMove: (move) => executeMove(move),
        playerTurn: currentPlayer === 'white' ? 2 : 1, // mapping 'white' to 2 for bot
        gameStarted,
        gameOver,
        botStrategy: getBotMove,
        gameState: { board, currentPlayer },
        difficulty,
        botPlayerId: 2 // Computer is always White/Player 2
    });


    // Wrapper for click
    const handleCellClick = (r, c) => {
        if (!gameStarted || gameOver || isBotThinking) return;
        if (currentPlayer === 'white' && gameMode === 'PvC') return; // Enforce AI turn
        
        // Validate
        if (!validMoves.some(m => m.r === r && m.c === c)) return;

        executeMove({ r, c });
    };

    const isValidCell = (r, c) => {
        return validMoves.some(m => m.r === r && m.c === c);
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                <button
                    onClick={() => (onBack ? onBack() : navigate("/games"))}
                    className="flex items-center text-blue-600 hover:text-blue-800 font-medium mb-6"
                >
                    <ArrowLeft className="w-5 h-5 mr-1" />
                    Back to Games
                </button>

                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    {/* Header */}
                    <div className="bg-green-600 p-6 text-white text-center relative">
                        <h1 className="text-3xl font-bold mb-2">Reversi</h1>
                        <p className="text-green-100">Outflank your opponent and dominate the board!</p>
                        <button
                            onClick={() => setShowRules(!showRules)}
                            className="absolute right-6 top-6 p-2 bg-green-700 rounded-full hover:bg-green-800 transition-colors"
                            title="Game Rules"
                        >
                            <Info className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Rules Modal */}
                     {showRules && (
                        <div className="bg-green-50 p-6 border-b border-green-100 animate-slide-down">
                            <h3 className="font-bold text-green-800 mb-2">How to Play:</h3>
                            <ul className="list-disc list-inside space-y-1 text-sm text-green-800">
                                <li>You are <strong>{getPlayerName('black')}</strong> (Black).</li>
                                <li>Place your disc to flank one or more opponent discs between two of yours.</li>
                                <li><strong>Corners</strong> are valuable! (Strategy Tip)</li>
                                <li>The game ends when the board is full or no moves are possible.</li>
                            </ul>
                        </div>
                    )}


                    <div className="p-8">
                        {!gameStarted ? (
                            <div className="space-y-8">
                                <GameOptions
                                    gameMode={gameMode}
                                    setGameMode={setGameMode}
                                    difficulty={difficulty}
                                    setDifficulty={setDifficulty}
                                    onStart={initializeGame}
                                />
                                <PlayerNameInput
                                    player1Name={player1Name}
                                    setPlayer1Name={setPlayer1Name}
                                    player2Name={player2Name}
                                    setPlayer2Name={setPlayer2Name}
                                    gameMode={gameMode}
                                />
                            </div>
                        ) : (
                            <>
                                {/* Status Bar */}
                                <div className="flex flex-col sm:flex-row justify-between items-center mb-8 bg-gray-100 p-4 rounded-lg gap-4">
                                     {/* Player 1 (Black) */}
                                    <div className={`flex items-center space-x-3 px-6 py-3 rounded-lg w-full sm:w-auto transition-all ${currentPlayer === 'black' ? 'bg-black text-white shadow-lg scale-105' : 'bg-white text-gray-500 border border-gray-200 opacity-60'}`}>
                                        <div className="text-left">
                                            <div className="flex items-center gap-2">
                                                <User className="w-4 h-4" />
                                                <span className="text-xs font-bold uppercase tracking-wider">{getPlayerName('black')}</span>
                                            </div>
                                            <p className="text-3xl font-bold mt-1">{scores.black}</p>
                                        </div>
                                    </div>

                                    {/* Middle Status */}
                                    <div className="text-center">
                                        {!gameOver ? (
                                            <div className="font-medium text-gray-600 flex flex-col items-center">
                                                {isBotThinking ? (
                                                    <span className="flex items-center text-purple-600 animate-pulse">
                                                        <Cpu className="w-5 h-5 mr-2" />
                                                        Thinking...
                                                    </span>
                                                ) : (
                                                    <span className="text-lg">
                                                        It's <span className={`font-bold ${currentPlayer === 'black' ? 'text-black' : 'text-green-600'}`}>{getPlayerName(currentPlayer)}</span>'s turn
                                                    </span>
                                                )}
                                                {/* Pass Turn Indicator */}
                                                {!isBotThinking && validMoves.length === 0 && !gameOver && (
                                                    <span className="text-red-500 font-bold text-sm mt-1 animate-bounce">
                                                        No moves! Passing turn...
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="animate-bounce-in flex flex-col items-center">
                                                <Trophy className={`w-8 h-8 mb-1 ${winner === 'draw' ? 'text-gray-600' : 'text-yellow-500'}`} />
                                                <span className="font-bold text-xl text-gray-800">
                                                    {winner === 'draw' ? "It's a Draw!" : `${getPlayerName(winner)} Wins!`}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Player 2 (White) */}
                                    <div className={`flex items-center justify-end space-x-3 px-6 py-3 rounded-lg w-full sm:w-auto transition-all ${currentPlayer === 'white' ? 'bg-white text-green-800 border-2 border-green-600 shadow-lg scale-105' : 'bg-white text-gray-500 border border-gray-200 opacity-60'}`}>
                                        <div className="text-right">
                                            <div className="flex items-center justify-end gap-2">
                                               <span className="text-xs font-bold uppercase tracking-wider">{getPlayerName('white')}</span>
                                               {gameMode === 'PvC' ? <Cpu className="w-4 h-4 ml-1" /> : <User className="w-4 h-4 ml-1" />}
                                            </div>
                                            <p className="text-3xl font-bold mt-1">{scores.white}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Game Board */}
                                <div className="flex justify-center mb-8">
                                    <div className="bg-green-800 p-2 rounded-lg shadow-2xl relative">
                                        <div
                                            className="grid grid-cols-8 gap-0.5 bg-green-900 border-4 border-green-800"
                                            style={{ width: 'min(90vw, 450px)', height: 'min(90vw, 450px)' }}
                                        >
                                            {board.map((row, r) => (
                                                row.map((cell, c) => {
                                                    const valid = isValidCell(r, c) && !isBotThinking && (gameMode === 'PvP' || currentPlayer === 'black');
                                                    
                                                    return (
                                                        <div
                                                            key={`${r}-${c}`}
                                                            onClick={() => handleCellClick(r, c)}
                                                            className={`
                                                                bg-green-600 flex items-center justify-center relative cursor-pointer
                                                                transition-colors duration-200
                                                                ${valid ? 'hover:bg-green-500' : ''}
                                                            `}
                                                        >
                                                             {/* Grid Texture / Dot */}
                                                             {/* Optional standard Othello dots at (2,2), (2,6), (6,2), (6,6) */}
                                                             { ([2,6].includes(r) && [2,6].includes(c)) && (
                                                                <div className="absolute w-1.5 h-1.5 bg-green-900 rounded-full opacity-30" />
                                                             )}

                                                            {/* Valid Move Hint */}
                                                            {valid && (
                                                                <div className="absolute w-3 h-3 bg-black opacity-10 rounded-full animate-pulse transform scale-75" />
                                                            )}

                                                            {/* Piece */}
                                                            {cell && (
                                                                <div
                                                                    className={`
                                                                        w-[85%] h-[85%] rounded-full shadow-[inset_-3px_-3px_5px_rgba(0,0,0,0.5),2px_2px_5px_rgba(0,0,0,0.3)]
                                                                        transition-all duration-500 transform
                                                                        ${cell === 'black' ? 'bg-gray-900 rotate-0' : 'bg-gray-100 rotate-180 border border-gray-300'}
                                                                    `}
                                                                >
                                                                     {/* Inner detail for 3D look */}
                                                                     <div className={`absolute top-2 left-2 w-1/3 h-1/3 rounded-full bg-white opacity-20 filter blur-[1px]`} />
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Controls */}
                                <div className="flex justify-center space-x-4">
                                    <button
                                        onClick={() => setGameStarted(false)}
                                        className="flex items-center space-x-2 px-6 py-2 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-colors shadow-sm font-medium"
                                    >
                                        <RefreshCw className="w-5 h-5" />
                                        <span>Restart Game</span>
                                    </button>
                                </div>
                            </>
                        )}
                        
                        <div className="mt-8 text-center text-gray-400 text-xs">
                           Reversi - {gameMode === 'PvP' ? '2 Player' : `Vs Computer (${difficulty})`}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReversiGame;
